/**
 * Baseline rules that every avatar inherits from. Specific avatar
 * modules override only what they change; the resolver deep-merges
 * their override onto this object.
 *
 * These defaults match the Sorcery: Contested Realm standard rules
 * (rulebook December 2025) when no avatar is selected. Effectively
 * the Sorcerer's profile, which is the rulebook's default avatar.
 *
 * When adding a new rule category: add the default here first, then
 * update the `AvatarRules` JSDoc in `index.js` so future avatar files
 * get autocomplete for the new field.
 */
export const DEFAULT_AVATAR_RULES = Object.freeze({
  openingHand: Object.freeze({
    // Cards drawn from Spellbook at game start, before any mulligan.
    spellbook: 3,
    // Cards drawn from Atlas at game start, before any mulligan.
    atlas: 3,
  }),

  mulligan: Object.freeze({
    // Maximum cards (across both decks combined) that may be returned
    // to deck bottoms in a single mulligan. Sorcery's July 2024 rule
    // capped this at 3. Previously it was unlimited per deck.
    maxCards: 3,
    // How many mulligans may be taken per game. Sorcery is one-and-done.
    allowedCount: 1,
  }),

  // Avatar starting life (HP tracker). All current avatars are 20,
  // but future sets may add variants.
  startingLife: 20,

  // Avatar starting mana. Sorcery starts players at 0 mana; mana rises
  // as sites are placed. Included here for completeness / future avatars.
  startingMana: 0,

  // --- Deck-building rules --------------------------------------------
  //
  // Consumed by src/utils/sorcery/deckRules.js when an avatar is known
  // at deck-edit time. Gameplay logic lives above; these only gate
  // deck construction / validation.
  //
  // All three fields below default to "standard rulebook behaviour".
  // avatars override only what deviates. The deckRules functions
  // (canAddCard, getMaxCopies) call through `resolveAvatarRules` and
  // then these fields; when no avatar is selected, they fall back to
  // these defaults and the builder behaves exactly as it did before
  // avatar rules existed.
  deckbuilding: Object.freeze({
    // Site cards normally go only in Atlas. Set true for Magician
    // (whose "spellbook may contain sites" text folds sites into
    // the spellbook zone).
    spellbookAllowsSites: false,

    // Atlas is normally required (every deck has a 30-card atlas).
    // Set true for Magician (no atlas at all).
    forbidAtlas: false,

    // Atlas per-card copy limit. Standard rulebook treats atlas like
    // any other zone (rarity-limited, usually up to 4 for Ordinary),
    // but Pathfinder's "atlas can't contain duplicates" clamps this
    // to 1. When null, the standard rarity-based max applies.
    atlasMaxCopies: null,

    // Deck-wide validation rules. Checked at save / match-entry time
    // via validateDeckStructure (deckRules.js), not per-card-add.
    // they require seeing the whole deck.
    requireUniquesOnly: false,  // Duplicator: every card must be Unique rarity.
    requirePairs: false,        // Duplicator: every card must appear exactly twice.

    // Imposter's "Banish an Avatar from your collection to mask
    // yourself" needs extra avatar cards to be parkable outside the
    // primary avatar slot. When true, the singleton rule only counts
    // entries with zone === 'avatar'. Additional avatars may live in
    // the spellbook or the collection (sideboard). All other avatars
    // keep the standard "one Avatar per deck" rule.
    allowExtraAvatars: false,
  }),

  // --- Extra activated abilities --------------------------------------
  //
  // Every avatar has the default "Tap → Play or draw a site" wired into
  // GameBoard._activatedEntriesFor as a _builtin. Some avatars carry
  // ADDITIONAL tap abilities on top. Sorcerer's "Tap → Draw a spell"
  // (v1.74) is the first. Each entry mirrors the parser-emitted
  // activated-entry shape that the existing action-menu pipeline
  // already consumes, plus a `_builtin` discriminator the dispatcher
  // routes on.
  //
  // Shape per entry:
  //   { id, trigger: 'activated', cost: [{ type: 'tap' | 'mana', amount? }],
  //     description, _builtin: '<dispatcher key>' }
  extraActivatedAbilities: Object.freeze([]),

  // --- Static aura entries -------------------------------------------
  //
  // Avatar-specific static (continuous) automation entries that don't
  // live in `public/sorcery-cards-automation.json`. The recompute walks
  // these alongside the catalog `automation` array so the engine
  // doesn't need a separate codepath. Today this is the home for
  // back-face aura entries on two-faced avatars (Druid's site-granted
  // damage trigger lives in `druid.back.staticAuras`). Future avatars
  // with passive whole-board effects can land entries here too.
  staticAuras: Object.freeze([]),

  // --- Builtin avatar site ability ------------------------------------
  //
  // Every Avatar carries an implicit "Tap → Play or draw a site"
  // ability that GameBoard's `_activatedEntriesFor` injects as a
  // `_builtin: 'avatar_site'` entry. Most avatars keep this and add
  // their unique abilities ON TOP. A few have card text that REPLACES
  // it (Pathfinder: "Tap → If able, play the topmost site of your atlas
  // to an adjacent location and move there."). Set this true for those
  // avatars; `_activatedEntriesFor` then skips the builtin entirely so
  // the action menu only surfaces the avatar's actual tap ability.
  replacesBuiltinAvatarSite: false,

  // --- Cast Magic as Spirit ------------------------------------------
  //
  // Animist's "You may cast magics in your hand as Spirits with power
  // equal to their cost." When true, a Magic card played from this
  // avatar's controller's hand is transformed at the hand → table
  // boundary into a Spirit Minion whose attack and defence both equal
  // the card's cost. The pure transform lives at
  // `engine/automation/ops/animistSpirit.js`; GameBoard's
  // `addCardToTable` applies it for cards flagged as coming from hand.
  castMagicAsSpirit: false,

  // --- Future extension point (not yet wired) -------------------------
  //
  // hooks: {
  //   onMatchStart(ctx),
  //   onOpeningHandDrawn(ctx),
  //   onTurnStart(ctx, { player }),
  //   onTurnEnd(ctx, { player }),
  //   onCardDrawn(ctx, { card, fromPile }),
  //   onCardPlayed(ctx, { card }),
  //   onCardLeavingPlay(ctx, { card, to }),
  //   onAvatarTapped(ctx),
  //   onDamageDealt(ctx, { source, target, amount }),
  // }
  //
  // When the first avatar that needs imperative behaviour lands
  // (e.g. Witch's curse, Enchantress's auto-animate-on-spell-cast),
  // add an EventEmitter-style hooks registry to GameBoard and fire
  // the relevant trigger points. Until then, the shape is documented
  // here so new avatar files have a consistent target.
  hooks: Object.freeze({}),
});
